/**
 * Provisioning Modal Component
 * Shown after successful payment to indicate provisioning is in progress
 */

import React from 'react';
import { XMarkIcon, CheckCircleIcon, ClockIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { ReservationResponse } from '../../types/marketplace';

interface ProvisioningModalProps {
  reservation: ReservationResponse;
  onClose: () => void;
  onViewReservations?: () => void;
}

const ProvisioningModal: React.FC<ProvisioningModalProps> = ({
  reservation,
  onClose,
  onViewReservations,
}) => {
  const formatPrice = (price: number) => `₹${price.toFixed(2)}`;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Payment Successful!</h2>
                <p className="text-sm text-gray-400">Order #{reservation.code}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-400 rounded-lg hover:bg-gray-800"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Banner */}
            <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <ClockIcon className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-300">Provisioning in Progress</p>
                <p className="text-sm text-amber-400/80">
                  Your GPU instance is being set up. This typically takes 30-60 minutes.
                </p>
              </div>
            </div>

            {/* Order Details */}
            <div className="bg-gray-800 rounded-xl p-4 space-y-3">
              <h3 className="font-semibold text-white">Order Summary</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-400">GPU Type</p>
                  <p className="font-medium text-white">{reservation.gpu.name}</p>
                </div>
                <div>
                  <p className="text-gray-400">GPU Count</p>
                  <p className="font-medium text-white">{reservation.configuration.gpuCount}</p>
                </div>
                <div>
                  <p className="text-gray-400">Configuration</p>
                  <p className="font-medium text-white">
                    {reservation.configuration.cpuCores} CPU, {reservation.configuration.ramGB} GB RAM
                  </p>
                </div>
                <div>
                  <p className="text-gray-400">Duration</p>
                  <p className="font-medium text-white">{reservation.timing.durationHours} hours</p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Amount Paid</span>
                  <span className="text-xl font-bold text-white">
                    {formatPrice(reservation.payment.amount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Email Notice */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <EnvelopeIcon className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-300">You'll receive an email</p>
                <p className="text-sm text-blue-400/80">
                  Once your instance is ready, we'll send you an email with SSH access details
                  and connection instructions.
                </p>
              </div>
            </div>

            {/* What's Next */}
            <div>
              <h3 className="font-semibold text-white mb-3">What happens next?</h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    1
                  </span>
                  <div>
                    <p className="font-medium text-white">Order confirmed</p>
                    <p className="text-sm text-gray-400">Your payment has been processed</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 animate-pulse">
                    2
                  </span>
                  <div>
                    <p className="font-medium text-white">Instance provisioning</p>
                    <p className="text-sm text-gray-400">Your GPU instance is being configured</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                    3
                  </span>
                  <div>
                    <p className="font-medium text-gray-400">Ready to connect</p>
                    <p className="text-sm text-gray-500">SSH details sent via email</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-800 bg-gray-800/50 rounded-b-2xl flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
            {onViewReservations && (
              <button
                onClick={onViewReservations}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
              >
                View My Reservations
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvisioningModal;
